import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MOB_EVENT_TYPE_EVENT } from '../../config/const';
import { mob } from '../../utils/logger';

const ResponseAPIGuideJavaScript = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Getting Started</p>
        <h1>Response API Guide - JavaScript</h1>
      </div>

      <div className="info-banner">
        <i className="fa-solid fa-info-circle"></i>
        <div>
          <p>Heads up! We launched MemU Platform. You can now use MemU without any code.</p>
        </div>
      </div>

      <p>MemU offers three ways to leverage our memory system: <strong>our managed platform</strong>, <strong>our enterprise solution</strong>, and <strong>our open source solution</strong>.</p>

      <h2 id="installation">Step 1: Installation and Basic Setup</h2>

      <h3>Using npm</h3>
      <div className="code-block">
        <SyntaxHighlighter
          language="bash"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          npm install memu-js
        </SyntaxHighlighter>
      </div>

      <h3 id="api-key-setup">Step 2: API Key Setup</h3>
      <p>
        To obtain your API key from the MemU platform, follow these steps:
      </p>

      <div className="setup-steps">
        <div className="step-item">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Sign in to MemU Platform</h4>
            <p>Visit <a href="https://app.memu.so/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => mob(
                'memu_pro_ev_quick_start_to_console_click',
                MOB_EVENT_TYPE_EVENT,
                {
                  source: 'response',
                  source_lang: 'js'
                },
                true)
              }>https://app.memu.so/
            </a> and sign in to your account.</p>
          </div>
        </div>

        <div className="step-item">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Switch to Response API</h4>
            <p>In your MemU dashboard, navigate to the API section and select "Response API" to enable the unified conversation endpoint.</p>
          </div>
        </div>

        <div className="screenshot-container">
          <img
            src="/static/changeresponse.png"
            alt="MemU Dashboard Switch to Response API"
            className="screenshot"
          />
        </div>

        <div className="step-item">
          <div className="step-number">3</div>
          <div className="step-content">
            <h4>Check Your Billing Balance</h4>
            <p>Before proceeding, make sure you have sufficient credits in your account. Check your billing dashboard to confirm your remaining balance.</p>
            <p>
            Want to learn more about Response API pricing? </p>
            <a href="/pricing#response-api" className="pricing-link" target="_blank" rel="noopener noreferrer">Check pricing →</a>
          
          </div>
        </div>

        <div className="screenshot-container">
          <img
            src="/static/billing.png"
            alt="MemU Billing Dashboard"
            className="screenshot"
          />
          <div className="screenshot-caption">
            Check your remaining credits in the billing dashboard
          </div>
        </div>



        <div className="step-item">
          <div className="step-number">4</div>
          <div className="step-content">
            <h4>Copy your API Key from the dashboard</h4>
            <p>Once logged in, navigate to your dashboard and copy your unique API key.</p>
          </div>
        </div>

        <div className="screenshot-container">
          <img
            src="/static/apikeyscreenshot.jpg"
            alt="MemU Dashboard API Key Location"
            className="screenshot"
          />
          <div className="screenshot-caption">
            Copy your API key from the dashboard
          </div>
        </div>
      </div>



      <h4>Set your API key as an environment variable</h4>
      <div className="code-block">
          <SyntaxHighlighter
              language="bash"
              style={vscDarkPlus}
              customStyle={{
                  margin: 0,
                  padding: 0,
                  background: 'transparent',
                  fontSize: '14px',
                  lineHeight: '1.5'
              }}
          >
              export MEMU_API_KEY="your_api_key_here"
          </SyntaxHighlighter>
      </div>

      <h3 id="initialize-client">Step 3: Initialize Client</h3>
      <p>Once you have your API key, initialize the MemU client in your application:</p>
      
      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            background: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {`import { MemuClient } from 'memu-js';

// Initialize MemU client
const memuClient = new MemuClient({
    baseUrl: "https://api.memu.so",
    apiKey: process.env.MEMU_API_KEY
});

// Verify connection
console.log("MemU client initialized successfully!");`}
        </SyntaxHighlighter>
      </div>

      <h3 id="response-api">Step 4: Response API - All-in-One Solution</h3>
      <p>
        MemU's Response API provides a complete conversational AI solution with built-in memory management.
        This all-in-one approach eliminates the need for manual memory operations and context management.
      </p>

      <div className="feature-highlight-box">
        <h4>🚀 Key Benefits</h4>
        <ul>
          <li><strong>No Manual Memory Management:</strong> No need to call retrieve_memory or memorize_conversation</li>
          <li><strong>Automatic Context Handling:</strong> System intelligently manages conversation context</li>
          <li><strong>Simplified Integration:</strong> Single API call for complete conversational AI</li>
        </ul>
      </div>

      <div className="code-block">
        <SyntaxHighlighter
          language="javascript"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
{`import { MemuClient } from 'memu-js';
import readline from 'readline';

// Initialize MemU client
const memuClient = new MemuClient({
    baseUrl: "https://api.memu.so",
    apiKey: "your_api_key_here"
});

// Create readline interface for user input
const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout
});


const chatLoop = () => {
  rl.question('User: ', async (userInput) => {
      try {
          const response = await memuClient.chat({
              userId: 'test_user1',
              agentId: 'test_agent1',
              message: userInput
          });
          
          // Display the complete response structure for debugging
          console.log('Full response:', JSON.stringify(response, null, 2));
          
          // Try to extract the actual message content
          const messageContent = response.content || response.message || response.data || response;
          console.log(\`Assistant: \${messageContent}\`);
          chatLoop(); // Continue the conversation
      } catch (error) {
          console.error('Error:', error);
          chatLoop();
      }
  });
};

// Start the conversation loop
chatLoop();`}
        </SyntaxHighlighter>
      </div>

      <h2 id="platform-options">Platform Options</h2>

      <div className="platform-cards">
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-chart-bar"></i>
          </div>
          <h3>MemU Platform (Managed Solution)</h3>
          <p>Better, faster, fully managed, and hassle free solution.</p>
        </div>

        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-code-branch"></i>
          </div>
          <h3>MemU Open Source</h3>
          <p>Self hosted, fully customizable, and open source.</p>
        </div>
      </div>

      <h2>Need help?</h2>
      <p>If you have any questions, please feel free to reach out to us using one of the following methods:</p>

      <div className="help-links">
        <div className="help-item">
          <h3>Discord</h3>
          <p>Join our community</p>
          <a href="https://discord.gg/hQZntfGsbJ" className="help-link">Join Discord →</a>
        </div>

        <div className="help-item">
          <h3>GitHub</h3>
          <p>Ask questions on GitHub</p>
          <a href="#github" className="help-link">Visit GitHub →</a>
        </div>

        <div className="help-item">
          <h3>Support</h3>
          <p>Get direct support</p>
          <a href="mailto:info@nevamind.ai" className="help-link">Contact Support →</a>
        </div>
      </div>
    </div>
  );
};

export default ResponseAPIGuideJavaScript;
